import { useMutation, useQuery } from 'react-query';
import { client } from 'src/utils/api-client';
import {
  EsgDmaImpactInSchema,
  EsgDmaImpactOutSchema,
  EsgDmaRiskOpportunityInSchema,
  EsgDmaRiskOpportunityOutSchema,
  EsgDmaStakeholdersAnswersAnalyticsSchema,
  EsgDmaSubtopicOutSchema,
  EsgDmaTopicHierarchySchema,
  EsgDmaTopicOutSchema,
  EsgStakeholderAnswerInSchema,
  EsgStakeholderAnswerOutSchema,
  EsgStakeholderDataInSchema,
  EsgStakeholderDataOutSchema,
  EsgDmaStakeholderQualityAnswerInSchema,
  EsgDmaStakeholderQualityAnswerOutSchema,
  EsgDmaValueChainEntityOutSchema,
  EsgDmaValueChainEntityInSchema,
  ValueChainEmployeesByCountryInSchema,
  ValueChainEmployeesByCountryOutSchema,
  ValueChainStakeholderTypeInSchema,
  ValueChainStakeholderTypeOutSchema,
  EsgDmaOutSchema,
  EsgDmaInSchema,
  EsgDmaRiskOpportunityMatterDataInSchema,
  EsgDmaRiskOpportunityMatterDataOutSchema,
  EsgDmaImpactMatterDataInSchema,
  EsgDmaImpactMatterDataOutSchema,
  EsgDatapointMaterialityOutSchema,
  EsgStakeholderSurveyOutSchema,
  EsgDmaMatrixDataSchema,
  EsgDmaOverriddenSubTopicOutSchema,
  EsgDmaOverriddenSubSubTopicOutSchema,
  EsgStakeholderOverriddenSubTopicSchema,
  EsgStakeholderOverriddenSubSubTopicSchema,
  EsgDmaSubtopicInSchema,
  EsgDmaSubSubTopicOutSchema,
  EsgDmaSubSubTopicInSchema,
  EsgStakeholderAnswerCommentsOutSchema,
  EsgStakeholderAnswerCommentsInSchema,
  EsgStakeholderAnswerCommentsFilterSchema,
  EsgDmaAdditionalFileOutSchema,
  EsgDmaAdditionalFileInSchema,
  EsgDmaMatterReasoningOutSchema,
  EsgDmaMatterReasoningFilterSchema,
  EsgDmaMatterReasoningInSchema,
} from './types';
import { parseStages23Hierarchy } from './utils';
import {
  QueryOptions,
  useExportToFileMutation,
  useLanguage,
  useResourceController,
} from 'src/common/hooks';
import { UUID } from 'crypto';
import { useParams } from 'react-router-dom';
import { useEsgReports } from '../hooks';

const staticRequestsQueryOptions = {
  staleTime: 1000 * 60 * 60, // 1 hour
  cacheTime: 1000 * 60 * 60, // 1 hour
};

const dynamicRequestsQueryOptions = {
  staleTime: 1000 * 60 * 5, // 5 minutes
  cacheTime: 1000 * 60 * 10, // 10 minutes
};

export const useCurrentDma = () => {
  const { reportId } = useParams();
  const { _instance: report } = useEsgReports({ id: Number(reportId) });
  const { _instance: dma } = useEsgDmas({ id: report?.esg_dma_id });

  return { dma, report };
};

export const useDmaHierarchy = (options: { esg_dma_id?: UUID }) => {
  const { language } = useLanguage();
  const query = useQuery(
    ['dma', 'hierarchy', { language, options }],
    () =>
      client
        .get<EsgDmaTopicHierarchySchema[]>('/web/esg/v3/dma-hierarchy', {
          params: { esg_dma_id: options.esg_dma_id },
        })
        .then((res) => res.data),
    {
      enabled: !!options.esg_dma_id,
      ...dynamicRequestsQueryOptions,
    }
  );

  const topics = query.data || [];

  // hierarchy for stages 2 and 3
  // is only 2 levels: TitleRow -> (SubtopicRow or SubSubTopicRow) -> ImpactRow
  const stages23Hierarchy = parseStages23Hierarchy(topics);

  return { topics, stages23Hierarchy, ...query };
};

export function useStakeholderTopics(esg_stakeholder_data_id?: number) {
  const { language } = useLanguage();

  // --- Queries ---
  const subTopicsQuery = useQuery({
    queryKey: ['dmaOverriddenSubTopics', esg_stakeholder_data_id, language],
    queryFn: () =>
      client
        .get<EsgDmaOverriddenSubTopicOutSchema[]>(
          `/web/esg/v3/stakeholders/data/${esg_stakeholder_data_id}/sub_topics`
        )
        .then((res) => res.data),
    enabled: !!esg_stakeholder_data_id,
  });

  const subSubTopicsQuery = useQuery({
    queryKey: ['dmaOverriddenSubSubTopics', esg_stakeholder_data_id, language],
    queryFn: () =>
      client
        .get<EsgDmaOverriddenSubSubTopicOutSchema[]>(
          `/web/esg/v3/stakeholders/data/${esg_stakeholder_data_id}/sub_sub_topics`
        )
        .then((res) => res.data),
    enabled: !!esg_stakeholder_data_id,
  });

  // --- Mutations ---
  const subtopicMutation = useMutation({
    mutationFn: (data: EsgStakeholderOverriddenSubTopicSchema) =>
      client.put(
        `/web/esg/v3/stakeholders/data/${esg_stakeholder_data_id}/sub_topics`,
        data
      ),
    onSuccess: () => {
      subTopicsQuery.refetch();
      subSubTopicsQuery.refetch();
    },
  });

  const subSubtopicMutation = useMutation({
    mutationFn: (data: EsgStakeholderOverriddenSubSubTopicSchema) =>
      client.put(
        `/web/esg/v3/stakeholders/data/${esg_stakeholder_data_id}/sub_sub_topics`,
        data
      ),
    onSuccess: () => {
      subTopicsQuery.refetch();
      subSubTopicsQuery.refetch();
    },
  });

  return {
    subTopics: subTopicsQuery.data ?? [],
    subSubTopics: subSubTopicsQuery.data ?? [],
    subTopicsQuery,
    subSubTopicsQuery,
    updateSubTopic: subtopicMutation,
    updateSubSubTopic: subSubtopicMutation,
  };
}

export const useRiskOpportunities = (
  queryParams: Partial<EsgDmaRiskOpportunityOutSchema> = {},
  queryOptions: QueryOptions<EsgDmaRiskOpportunityOutSchema> = dynamicRequestsQueryOptions
) =>
  useResourceController<
    EsgDmaRiskOpportunityInSchema,
    EsgDmaRiskOpportunityOutSchema
  >(
    `/web/esg/v3/dma-risk-opportunities`,
    ['dma', 'matter', 'risk-opportunity', 'entities', queryParams],
    queryParams,
    { invalidateKey: ['dma', 'matter'], ...queryOptions }
  );

export const useDmaImpacts = (
  queryParams: Partial<EsgDmaImpactOutSchema> = {},
  queryOptions: QueryOptions<EsgDmaImpactOutSchema> = dynamicRequestsQueryOptions
) =>
  useResourceController<EsgDmaImpactInSchema, EsgDmaImpactOutSchema>(
    `/web/esg/v3/dma-impacts`,
    ['dma', 'matter', 'impact', 'entities', queryParams],
    queryParams,
    { invalidateKey: ['dma', 'matter'], ...queryOptions }
  );

export const useDmaMatterReasonings = (
  queryParams: Partial<EsgDmaMatterReasoningFilterSchema> = {},
  queryOptions: QueryOptions<EsgDmaMatterReasoningOutSchema> = dynamicRequestsQueryOptions
) =>
  useResourceController<
    EsgDmaMatterReasoningInSchema,
    EsgDmaMatterReasoningOutSchema
  >(
    `/web/esg/v3/dma-matter-reasonings`,
    ['dma', 'matter', 'dma-matter-reasonings', queryParams],
    queryParams,
    {
      invalidateKey: ['dma', 'matter', 'dma-matter-reasonings'],
      ...queryOptions,
    }
  );

export const useEsgStakeholderAnswer = (
  queryParams: Partial<EsgStakeholderAnswerOutSchema> = {},
  queryOptions: QueryOptions<EsgStakeholderAnswerOutSchema> = dynamicRequestsQueryOptions
) =>
  useResourceController<
    EsgStakeholderAnswerInSchema,
    EsgStakeholderAnswerOutSchema
  >(
    `/web/esg/v3/stakeholders-answers`,
    ['dma', 'stakeholders', 'survey-answers', queryParams],
    queryParams,
    queryOptions
  );

export const useEsgStakeholderAnswerComments = (
  queryParams: Partial<EsgStakeholderAnswerCommentsFilterSchema> = {},
  queryOptions: QueryOptions<EsgStakeholderAnswerCommentsOutSchema> = dynamicRequestsQueryOptions
) =>
  useResourceController<
    EsgStakeholderAnswerCommentsInSchema,
    EsgStakeholderAnswerCommentsOutSchema
  >(
    `/web/esg/v3/stakeholders-answers-comments`,
    ['dma', 'stakeholders', 'survey-comments', queryParams],
    queryParams,
    {
      invalidateKey: ['dma', 'stakeholders', 'survey-comments'],
      ...queryOptions,
    }
  );

export const useEsgSubSubTopic = (
  queryParams: Partial<{ esg_dma_id: UUID }> = {},
  queryOptions: QueryOptions<EsgDmaSubSubTopicOutSchema> = dynamicRequestsQueryOptions
) =>
  useResourceController<EsgDmaSubSubTopicInSchema, EsgDmaSubSubTopicOutSchema>(
    '/web/esg/v3/dma-hierarchy/subsubtopics',
    ['dma', 'subsubtopics', queryParams],
    queryParams,
    queryOptions
  );

export const useEsgSubTopic = (
  queryParams: Partial<{ esg_dma_id: UUID }> = {},
  queryOptions: QueryOptions<EsgDmaSubtopicOutSchema> = dynamicRequestsQueryOptions
) => {
  const { language } = useLanguage();
  const url = `/web/esg/v3/dma-hierarchy/subtopics`;
  const queryKey = ['dma', 'subtopics', language];

  const controller = useResourceController<
    EsgDmaSubtopicInSchema,
    EsgDmaSubtopicOutSchema
  >(url, [queryKey, queryParams], queryParams, queryOptions);

  const subTopics = controller._data || [];
  const stakeholderSurveySubtopics = subTopics.filter(
    (item) => item.is_stakeholder_survey
  );
  return {
    stakeholderSurveySubtopics,
    subTopics: controller._data,
    subTopicsMap: controller._dataMap,
    controller,
  };
};

export const useEsgTopic = () => {
  const { language } = useLanguage();
  const url = `/web/esg/v3/dma-hierarchy/topics`;
  const queryKey = ['dma-topics', language];

  const query = useQuery(
    queryKey,
    () => client.get<EsgDmaTopicOutSchema[]>(url).then((res) => res.data),
    staticRequestsQueryOptions
  );
  const topics = query.data || [];
  const topicsMap = topics.reduce(
    (acc: Record<number, EsgDmaTopicOutSchema>, item) => {
      acc[item.id] = item;
      return acc;
    },
    {}
  );
  return { topics, topicsMap, ...query };
};

export const useEsgStakeholderSurveys = (
  queryParams: Partial<EsgStakeholderSurveyOutSchema> = {},
  queryOptions: QueryOptions<EsgStakeholderSurveyOutSchema> = dynamicRequestsQueryOptions
) =>
  useResourceController<undefined, EsgStakeholderSurveyOutSchema>(
    `/web/esg/v3/stakeholders-survey`,
    ['dma', 'stakeholders', 'surveys', queryParams],
    queryParams,
    queryOptions
  );

export const useEsgStakeholdersAnswersAnalytics = (
  esg_dma_id?: UUID,
  esg_stakeholder_id?: number
) => {
  const params: Record<string, any> = { esg_dma_id };
  if (esg_stakeholder_id) {
    params.esg_stakeholder_id = esg_stakeholder_id;
  }

  const url = `/web/esg/v3/stakeholders-answers-analytics`;
  const queryKey = [
    'dma',
    'stakeholders',
    'answers-analytics',
    { esg_dma_id, esg_stakeholder_id },
  ];

  const query = useQuery(
    queryKey,
    () =>
      client
        .get<EsgDmaStakeholdersAnswersAnalyticsSchema>(url, { params })
        .then((res) => res.data),
    {
      ...dynamicRequestsQueryOptions,
      enabled: !!esg_dma_id,
    }
  );

  const analytics = query.data || {
    responses_count: 0,
    surveyed_stakeholder_types_count: 0,
    surveyed_stakeholder_types: [],
    stakeholder_types_with_responses_count: 0,
    summary: [],
    topic_summary: [],
    survey_answers_summary: [],
  };

  return { analytics, ...query };
};

export const useEsgStakeholderData = (
  queryParams: Partial<EsgStakeholderDataOutSchema> = {},
  queryOptions: QueryOptions<EsgStakeholderDataOutSchema> = dynamicRequestsQueryOptions
) =>
  useResourceController<
    EsgStakeholderDataInSchema,
    EsgStakeholderDataOutSchema
  >(
    `/web/esg/v3/stakeholders/data`,
    ['dma', 'stakeholders', 'data', queryParams],
    queryParams,
    queryOptions
  );

export const useDmaStakeholderQualityAnswers = (
  queryParams: Partial<EsgDmaStakeholderQualityAnswerInSchema> = {},
  queryOptions: QueryOptions<EsgDmaStakeholderQualityAnswerOutSchema> = dynamicRequestsQueryOptions
) =>
  useResourceController<
    EsgDmaStakeholderQualityAnswerInSchema,
    EsgDmaStakeholderQualityAnswerOutSchema
  >(
    `/web/esg/v3/quality-answers`,
    ['dma', 'stakeholders', 'quality-answers', queryParams],
    queryParams,
    queryOptions
  );

export const useDmaMatrix = (esgDmaId?: UUID, queryOptions: any = {}) => {
  const queryKey = ['dma', 'dma-matrix', { esgDmaId }];
  const query = useQuery(
    queryKey,
    () =>
      client
        .get<EsgDmaMatrixDataSchema>(`web/esg/v3/dma-matrix/${esgDmaId}`)
        .then((res) => res.data),
    {
      ...queryOptions,
      enabled: !!esgDmaId || !!queryOptions?.enabled,
      ...dynamicRequestsQueryOptions,
    }
  );

  return {
    _data: query.data?.datapoints || [],
    _threshold: query.data?.threshold,
    ...query,
  };
};

export const useEsgDmaValueChainEntities = (
  queryParams: Partial<EsgDmaValueChainEntityOutSchema> = {},
  queryOptions:
    | QueryOptions<EsgDmaValueChainEntityOutSchema>
    | undefined = undefined
) =>
  useResourceController<
    EsgDmaValueChainEntityInSchema,
    EsgDmaValueChainEntityOutSchema
  >(
    `/web/esg/v3/dma-value-chain-entities`,
    ['dma-value-chain-entities', queryParams],
    queryParams,
    queryOptions
  );

export const useEsgDmaValueChainEmployeesByCountry = (
  queryParams: Partial<ValueChainEmployeesByCountryOutSchema> & {
    entity_id: number;
  }
) =>
  useResourceController<
    ValueChainEmployeesByCountryInSchema,
    ValueChainEmployeesByCountryOutSchema
  >(
    `/web/esg/v3/dma-value-chain-employees-by-country`,
    ['dma-value-chain-employees-by-country', queryParams],
    queryParams
  );

export const useEsgDmaValueChainStakeholderTypes = (entityId?: number) =>
  useResourceController<
    ValueChainStakeholderTypeInSchema,
    ValueChainStakeholderTypeOutSchema
  >(
    `/web/esg/v3/dma-value-chain-stakeholder-types`,
    ['dma-value-chain-stakeholder-types', { entityId }],
    { entity_id: entityId }
  );

export const useEsgDmas = (
  queryParams: Partial<EsgDmaOutSchema> & { esg_report_id?: number } = {},
  queryOptions: QueryOptions<EsgDmaValueChainEntityOutSchema> = dynamicRequestsQueryOptions
) =>
  useResourceController<EsgDmaInSchema, EsgDmaOutSchema>(
    `/web/esg/v3/dmas`,
    ['dma', 'dmas', queryParams],
    queryParams,
    queryOptions
  );

export const useRiskOpportunityMatterData = (
  queryParams: Partial<EsgDmaRiskOpportunityMatterDataInSchema> = {},
  queryOptions: QueryOptions<EsgDmaRiskOpportunityMatterDataOutSchema> = dynamicRequestsQueryOptions
) =>
  useResourceController<
    EsgDmaRiskOpportunityMatterDataInSchema,
    EsgDmaRiskOpportunityMatterDataOutSchema
  >(
    `/web/esg/v3/dma-risk-opportunity-matter-data`,
    ['dma', 'matter', 'risk-opportunity', 'matter-data', queryParams],
    queryParams,
    {
      invalidateKey: ['dma', 'matter', 'risk-opportunity'],
      ...queryOptions,
    }
  );

export const useImpactMatterData = (
  queryParams: Partial<EsgDmaImpactMatterDataInSchema> = {},
  queryOptions: QueryOptions<EsgDmaImpactMatterDataOutSchema> = dynamicRequestsQueryOptions
) =>
  useResourceController<
    EsgDmaImpactMatterDataInSchema,
    EsgDmaImpactMatterDataOutSchema
  >(
    `/web/esg/v3/dma-impact-matter-data`,
    ['dma', 'matter', 'impact', 'matter-data', queryParams],
    queryParams,
    { invalidateKey: ['dma', 'matter', 'impact'], ...queryOptions }
  );

export const useDmaMaterialDatapoints = (esgDmaId?: UUID) => {
  const queryKey = ['dma', 'dma-material-datapoints', { esgDmaId }];
  const query = useQuery(
    queryKey,
    () =>
      client
        .get<EsgDatapointMaterialityOutSchema[]>(
          `web/esg/v3/dmas/${esgDmaId}/material-datapoints`
        )
        .then((res) => res.data),
    { enabled: !!esgDmaId, ...dynamicRequestsQueryOptions }
  );
  return query;
};

export async function getEsgDmaValueChainStakeholderTypes(
  entityId: number
): Promise<ValueChainStakeholderTypeOutSchema[]> {
  const params = { entity_id: entityId };

  try {
    const response = await client.get<ValueChainStakeholderTypeOutSchema[]>(
      '/web/esg/v3/dma-value-chain-stakeholder-types',
      { params }
    );
    return response.data;
  } catch (error) {
    console.error(
      `Failed to fetch stakeholder types for entity ID ${entityId}:`,
      error
    );
    throw error;
  }
}

export const useEsgDmaActions = (esgDmaId?: UUID) => {
  const url = `/web/esg/v3/dma-export/${esgDmaId}`;

  const _exportToCsv = (step: string) =>
    useExportToFileMutation(`${url}/${step}`);

  const exportStep2ToCsv = _exportToCsv('step-2');
  const exportStep3ToCsv = _exportToCsv('step-3');
  const exportStakeholderAnswersToCsv = _exportToCsv('stakeholder-answers'); // Step 4
  const exportStep5ToCsv = _exportToCsv('step-5');
  const exportDmaMatrixToCsv = _exportToCsv('dma-matrix');

  return {
    exportStep2ToCsv,
    exportStep3ToCsv,
    exportStakeholderAnswersToCsv,
    exportStep5ToCsv,
    exportDmaMatrixToCsv,
  };
};

export const useEsgDmaAdditionalFiles = (
  queryParams: Partial<EsgDmaAdditionalFileOutSchema> = {},
  queryOptions: QueryOptions<EsgDmaAdditionalFileOutSchema> = dynamicRequestsQueryOptions
) =>
  useResourceController<
    EsgDmaAdditionalFileInSchema,
    EsgDmaAdditionalFileOutSchema
  >(
    `/web/esg/v3/dma-additional-files`,
    ['dma', 'dma-additional-files', queryParams],
    queryParams,
    {
      invalidateKey: ['dma', 'dma-additional-files'],
      ...queryOptions,
    }
  );
